/**
 * AutoSaveIndicator Component
 * Shows the auto-save status and last saved time
 */

import { HTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'
import { Check, Cloud, AlertCircle } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

export type SaveStatus = 'saved' | 'saving' | 'error' | 'idle'

export interface AutoSaveIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  status: SaveStatus
  lastSaved?: Date
  error?: string
}

export const AutoSaveIndicator = forwardRef<HTMLDivElement, AutoSaveIndicatorProps>(
  ({ status, lastSaved, error, className, ...props }, ref) => {
    const getStatusContent = () => {
      switch (status) {
        case 'saving':
          return (
            <>
              <Cloud className="h-4 w-4 animate-pulse text-blue-500" />
              <span className="text-blue-600">Saving...</span>
            </>
          )
        case 'saved':
          return (
            <>
              <Check className="h-4 w-4 text-green-500" />
              <span className="text-green-600">
                Saved {lastSaved ? formatDistanceToNow(lastSaved, { addSuffix: true }) : ''}
              </span>
            </>
          )
        case 'error':
          return (
            <>
              <AlertCircle className="h-4 w-4 text-red-500" />
              <span className="text-red-600">{error || 'Save failed'}</span>
            </>
          )
        case 'idle':
        default:
          return (
            <>
              <Cloud className="h-4 w-4 text-gray-400" />
              <span className="text-gray-500">Not saved</span>
            </>
          )
      }
    }

    return (
      <div
        ref={ref}
        className={clsx('flex items-center gap-2 text-sm', className)}
        {...props}
      >
        {getStatusContent()}
      </div>
    )
  },
)

AutoSaveIndicator.displayName = 'AutoSaveIndicator'
